import { useIsMutating } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useLayerLocationHistory, useRouteLayer, useRouteLayerChildren } from "../../../../hooks/useCurrentEntities";

import { formatCheck2Frontend } from "../../../../actions/Checks/util";
import { isArtLocation } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { DeviceResultActions } from "../../../../actions/Tenants/config/constants";
import { useDefaultLayer } from "../../../../hooks";
import { useCreateCheckMutation } from "../../../../hooks/mutations/useCheckMutations";
import {
    useAddLayerAllocationMutation,
    useAddLayerChildMutation,
    useCreateSubLayerMutation,
} from "../../../../hooks/mutations/useLayerMutations";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import { buildChildOptions } from "../../../Forms/metaOptions";
import { Button } from "../../../Helper/Button";
import { SPECIAL_ACTIONS } from "./SpecialActions";
import { ShowLinkToLocationAlert } from "./StartCheckForm";
import { getLayerValue } from "./getLayerValue";

type CreateChildAndStartCheckProps = {
    child_type: string;
    check_location: string;
    allocate_to_stage?: string;
};

type CreateCheckData = Record<string, any>;

export default function CreateChildAndStartCheck({
    child_type,
    check_location,
    allocate_to_stage,
}: CreateChildAndStartCheckProps) {
    const layer = useRouteLayer();
    // The layer payload only carries the narrow child relation; the children query
    // is the one that flattens each child's meta onto the object.
    const children = getFilledArrayOrDefault(useRouteLayerChildren()?.results);
    const config = useConfig();
    const addLayerAllocationMutation = useAddLayerAllocationMutation();
    const addLayerChildMutation = useAddLayerChildMutation();
    const createSubLayerMutation = useCreateSubLayerMutation();
    const createCheckMutation = useCreateCheckMutation();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const navigate = useNavigate();

    const defaultLayer = useDefaultLayer()[0] as any;
    const layerIsUpdating = useIsMutating() > 0;
    const checkIsUpdating = useIsMutating() > 0;
    const { isLoading: locationHistoryLoading } = useLayerLocationHistory(tenantId, layer?.id);

    const [modalOpen, setModal] = useState(
        params.action === SPECIAL_ACTIONS.CREATE_CHILD_AND_START_CHECK,
    );
    const [createCheckData, setCheck] = useState<CreateCheckData>({});

    const defaultSubLayer = {
        ...defaultLayer,
        fruit_type: layer.fruit_type ? layer.fruit_type : defaultLayer.fruit_type,
        fruit_variety: layer.fruit_variety ? layer.fruit_variety : defaultLayer.fruit_variety,
        type: child_type,
        supplier: layer.supplier || "",
        country: layer.country || "",
        parent_meta: layer || {}, // Used in applyDisplayFilter
    };

    const [subLayerState, setSubLayer] = useState<any>(defaultSubLayer);

    useEffect(() => setSubLayer(defaultSubLayer), [
        defaultSubLayer.fruit_type,
        defaultSubLayer.fruit_variety,
        defaultSubLayer.supplier,
        defaultSubLayer.country,
    ]);

    const location_config: any = config.get_check_type_config(subLayerState, {
        location: check_location,
    });
    const shouldShowModal = isFilledArray(location_config?.meta_form_layer)
        || isFilledArray(location_config?.pre_check_form);

    // * we use this to copy data from the layer to the check e.g. box_size
    const check_meta_form = getFilledArrayOrDefault(location_config?.pre_check_form).map((field: any) => {
        const value = getLayerValue(subLayerState, field) ?? getLayerValue(layer, field);
        if (value !== undefined) {
            return { ...field, default_value: value };
        }
        return field;
    });

    useEffect(() => {
        const child = children.find((c: any) => c.label === subLayerState.label);
        if (!child) return;

        setSubLayer((prev: any) => ({ ...prev, ...child }));

        const childDefaults = getFilledArrayOrDefault(location_config?.pre_check_form).reduce((defaults: Record<string, any>, field: any) => {
            const value = getLayerValue(child, field);
            if (value !== undefined) {
                defaults[field.name] = value;
            }
            return defaults;
        }, {});
        setCheck((prev) => ({ ...prev, ...childDefaults }));
        // location_config is rebuilt on every render (new array/object refs), so key off
        // its stable `value` instead of `pre_check_form` to avoid an effect/setState loop.
    }, [children, subLayerState.label, location_config?.value]);

    const allocateToStage = (layer_id: string, stage_value?: string) => {
        if (!stage_value) return;
        if (stage_value !== layer.latest_location?.stage) {
            addLayerAllocationMutation.mutate({ tenantId, layerId: layer_id, stage: stage_value });
        }
    };

    const createAndStartCheck = (postCheck: any, theSubLayer: any) => {
        createCheckMutation.mutateAsync({ tenantId, data: postCheck }).then((check) => {

            if ((location_config?.flow || []).length === 0) {
                setModal(false);
                return;
            }
            navigate(
                `/layer/${theSubLayer.id}/add-check/${check.check_id}/${location_config?.flow?.[0]?.path}?from=/layer/${layer.id}`,
            );
        });
    };

    const _createLayerAndStartCheck = () => {
        if (layerIsUpdating || checkIsUpdating || locationHistoryLoading) return;

        const parentLayerId = layer.id;
        if (!parentLayerId) {
            return;
        }

        if (location_config.meta_form_layer
            && !metaFormIsValid(location_config.meta_form_layer, subLayerState)) {
            return;
        }

        if (check_meta_form.length > 0
            && !metaFormIsValid(check_meta_form, createCheckData)) {
            return;
        }

        if (allocate_to_stage) {
            allocateToStage(parentLayerId, allocate_to_stage);
        }

        createSubLayerMutation.mutateAsync({ tenantId, data: { ...subLayerState } }).then((theSubLayer) => {
            const latest_location_id = layer.latest_location?.location_id;

            if (allocate_to_stage) {
                addLayerAllocationMutation.mutate({
                    tenantId,
                    layerId: theSubLayer.id,
                    stage: allocate_to_stage,
                });
            }

            addLayerChildMutation.mutate({ layer, childLayerId: theSubLayer.id });

            const test: any = {
                layer_id: theSubLayer.id,
                location: location_config.value,
                // * Started from this page: the check is stored under this layer (e.g. the sales order)
                started_from_layer_id: layer.id,
            };

            if (location_config.is_linked_to_latest_location) {
                test.location_id = latest_location_id;
            }

            if (location_config.is_manual) {
                test.is_manual = true;
            }

            const { pressure_calibration_settings } = location_config;
            const requested_calibration_stage = pressure_calibration_settings?.calibration_stage
                || false;
            if (requested_calibration_stage) {
                test.requested_calibration_stage = requested_calibration_stage;
            }

            const postCheck = { ...test, ...createCheckData };

            if (isArtLocation(location_config.value)) {
                // * Fetch checks directly to see if there's a previous art check
                callAvosApi(`/layers/${theSubLayer.id}/checks`, {
                    params: { check_location: "advance_ripening" },
                }).then((response: any) => {
                    const checks = (response.data || []).map(formatCheck2Frontend);
                    const prevCheck = checks
                        .sort((a: any, b: any) => b.check_id - a.check_id)
                        .find((i: any) => i.location === location_config.value);
                    if (prevCheck) {
                        navigate(
                            `/layer/${theSubLayer.id}/add-check/${prevCheck.check_id}/advance_ripening`,
                        );
                    } else {
                        createAndStartCheck(postCheck, theSubLayer);
                    }
                }).catch(() => {
                    // If fetch fails, proceed to create check normally
                    createAndStartCheck(postCheck, theSubLayer);
                });
            } else {
                createAndStartCheck(postCheck, theSubLayer);
            }
        });
    };

    const isDisabled = () => {
        if (location_config.device_result_action === DeviceResultActions.pressure_advice
            && !config.is_location_calibrated(location_config, layer)) {
            return true;
        }
        if (location_config.device_result_action === DeviceResultActions.setup_pressure_calibration
            && config.is_location_calibrated(location_config, layer)) {
            return true;
        }
        if (layerIsUpdating || checkIsUpdating || locationHistoryLoading) return true;
        if (String((layer as any)[`start_${location_config.value}_disabled`]) === "1") {
            return true;
        }
        return false;
    };

    const startCheck = () => {
        if (shouldShowModal) {
            setModal(true);
            return;
        }
        _createLayerAndStartCheck();
    };

    const submit = () => {
        _createLayerAndStartCheck();
    };

    if (!location_config || location_config.is_invalid) return null;

    let { meta_form_layer } = location_config;
    if (isFilledArray(meta_form_layer) || isFilledArray(children)) {
        meta_form_layer = (meta_form_layer || []).map((item: any) => {
            if (item.type === "text-qrcode-scanner" && item.name === "label") {
                let filtered_children = children;
                // Filter by layer_type if specified
                if (item.layer_type) {
                    filtered_children = children.filter((child: any) => child.type === item.layer_type);
                }
                // * meta_options: child meta keys (e.g. batch) shown next to the label and searchable
                return {
                    ...item,
                    options: buildChildOptions(filtered_children, item.meta_options),
                };
            }
            return item;
        });
    }

    return (
        <div>
            <Button color="primary" disabled={isDisabled()} onClick={startCheck}>
                {location_config.text} check
            </Button>
            <Modal isOpen={modalOpen} size="md" toggle={() => setModal(false)}>
                <ModalHeader toggle={() => setModal(false)}>
                    {`${location_config.text + (isArtLocation(location_config.value) ? "" : " check")}`}
                </ModalHeader>
                <ModalBody>
                    <ShowLinkToLocationAlert location_config={location_config} layer={layer} />
                    <div>
                        {isFilledArray(meta_form_layer) && (
                            <MetaForm
                                key="layer"
                                meta={meta_form_layer}
                                setValue={(field: string, value: any) => setSubLayer((object: any) => ({
                                    ...object,
                                    [field]: value,
                                }))}
                                object={subLayerState}
                                config={config}
                            />
                        )}
                        {isFilledArray(location_config.pre_check_form) && (
                            <MetaForm
                                key="check"
                                meta={check_meta_form}
                                setValue={(field: string, value: any) => setCheck((object) => ({ ...object, [field]: value }))}
                                object={createCheckData}
                                extra_context={{
                                    fruit_variety: subLayerState.fruit_variety,
                                    fruit_type: subLayerState.fruit_type,
                                }}
                                config={config}
                            />
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setModal(false)}>
                        Cancel
                    </Button>
                    <Button disabled={isDisabled()} color="success" onClick={submit}>
                        Start
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

